import { useState } from "react";
import AdminLayout from "@/components/layout-admin";
import { useAdminSubmissions, useApartments, useDeleteSubmission } from "@/hooks/use-resources";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { DeleteConfirmDialog } from "@/components/ui/delete-confirm-dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { Search, Eye, Phone, Mail, User, Calendar, CheckCircle2, XCircle, CreditCard, Image as ImageIcon, Trash2 } from "lucide-react";
import type { Submission } from "@shared/schema";

const fetchSubmission = async (id: number) => {
  const res = await fetch(`/api/admin/submissions/${id}`);
  if (!res.ok) throw new Error("Failed");
  return res.json();
};

type ChecklistResult = { itemId: number; isChecked: boolean };
type PhotoResult = { requirementId: number; urls: string[] };

export default function AdminSubmissions() {
  const { data: submissions, isLoading } = useAdminSubmissions();
  const { data: apartments } = useApartments();
  const deleteSubmission = useDeleteSubmission();
  const { toast } = useToast();

  const [searchQuery, setSearchQuery] = useState("");
  const [apartmentFilter, setApartmentFilter] = useState("all");
  const [selectedSubmission, setSelectedSubmission] = useState<Submission | null>(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [enlargedImage, setEnlargedImage] = useState<string | null>(null);
  const [deleteConfirmSubmission, setDeleteConfirmSubmission] = useState<Submission | null>(null);

  const getApartmentName = (apartmentId: number) => {
    return apartments?.find((a) => a.id === apartmentId)?.name || "Unknown";
  };

  const formatDate = (timestamp: string | Date | null) => {
    if (!timestamp) return "N/A";
    const d = new Date(timestamp);
    return d.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
  };

  const sortedSubmissions = submissions
    ? [...submissions].sort(
        (a, b) =>
          new Date(b.timestamp || 0).getTime() -
          new Date(a.timestamp || 0).getTime()
      )
    : [];

  const filteredSubmissions = sortedSubmissions.filter((sub) => {
    const matchesSearch = sub.guestName
      .toLowerCase()
      .includes(searchQuery.toLowerCase());
    const matchesApartment =
      apartmentFilter === "all" ||
      sub.apartmentId === Number(apartmentFilter);
    return matchesSearch && matchesApartment;
  });

  const handleViewDetail = async (sub: Submission) => {
    setDetailLoading(true);
    setDetailOpen(true);
    try {
      const detail = await fetchSubmission(sub.id);
      setSelectedSubmission(detail);
    } catch {
      toast({
        title: "Error",
        description: "Failed to load submission details",
        variant: "destructive",
      });
      setDetailOpen(false);
    } finally {
      setDetailLoading(false);
    }
  };

  const hasFob = (sub: Submission) => !!sub.fobFrontUrl || !!sub.fobBackUrl;

  return (
    <AdminLayout>
      <div className="space-y-8">
        <div>
          <h1 className="text-3xl font-display font-bold" data-testid="text-page-title">
            Submissions
          </h1>
          <p className="text-muted-foreground mt-2">
            View all guest checkout submissions.
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-3">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder="Search by guest name..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9"
              data-testid="input-search-guest"
            />
          </div>
          <Select value={apartmentFilter} onValueChange={setApartmentFilter}>
            <SelectTrigger className="w-[200px]" data-testid="select-apartment-filter">
              <SelectValue placeholder="All Apartments" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Apartments</SelectItem>
              {apartments?.map((apt) => (
                <SelectItem key={apt.id} value={apt.id.toString()}>
                  {apt.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {isLoading ? (
          <div className="space-y-4">
            {[1, 2, 3].map((i) => (
              <Card key={i}>
                <CardContent className="p-6">
                  <div className="animate-pulse flex items-center gap-4">
                    <div className="h-10 w-10 bg-muted rounded-md" />
                    <div className="flex-1 space-y-2">
                      <div className="h-4 bg-muted rounded-md w-1/3" />
                      <div className="h-3 bg-muted rounded-md w-1/4" />
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        ) : filteredSubmissions.length === 0 ? (
          <Card>
            <CardContent className="p-12 text-center">
              <p className="text-muted-foreground">No submissions found.</p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-3">
            {filteredSubmissions.map((sub) => (
              <Card
                key={sub.id}
                className="hover-elevate cursor-pointer"
                data-testid={`card-submission-${sub.id}`}
                onClick={() => handleViewDetail(sub)}
              >
                <CardContent className="p-4 flex flex-wrap items-center justify-between gap-3">
                  <div className="flex items-center gap-4 min-w-0">
                    <div className="flex-shrink-0 w-10 h-10 rounded-md bg-primary/10 flex items-center justify-center">
                      <User className="w-5 h-5 text-primary" />
                    </div>
                    <div className="min-w-0">
                      <p
                        className="font-medium truncate"
                        data-testid={`text-guest-name-${sub.id}`}
                      >
                        {sub.guestName}
                      </p>
                      <div className="flex flex-wrap items-center gap-2 text-sm text-muted-foreground">
                        <span data-testid={`text-apartment-name-${sub.id}`}>
                          {getApartmentName(sub.apartmentId)}
                        </span>
                        <span>|</span>
                        <span data-testid={`text-submission-date-${sub.id}`}>
                          {formatDate(sub.timestamp)}
                        </span>
                        {sub.guestPhone && (
                          <>
                            <span>|</span>
                            <span
                              className="flex items-center gap-1"
                              data-testid={`text-guest-phone-${sub.id}`}
                            >
                              <Phone className="w-3 h-3" />
                              {sub.guestPhone}
                            </span>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge
                      variant={hasFob(sub) ? "default" : "secondary"}
                      className="text-xs"
                      data-testid={`badge-fob-status-${sub.id}`}
                    >
                      <CreditCard className="w-3 h-3 mr-1" />
                      {hasFob(sub) ? "FOB Submitted" : "No FOB"}
                    </Badge>
                    <Button
                      size="icon"
                      variant="ghost"
                      data-testid={`button-view-detail-${sub.id}`}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleViewDetail(sub);
                      }}
                    >
                      <Eye className="w-4 h-4" />
                    </Button>
                    <Button
                      size="icon"
                      variant="ghost"
                      className="text-destructive"
                      data-testid={`button-delete-submission-${sub.id}`}
                      onClick={(e) => {
                        e.stopPropagation();
                        setDeleteConfirmSubmission(sub);
                      }}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>

      <Dialog open={detailOpen} onOpenChange={setDetailOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Submission Details</DialogTitle>
          </DialogHeader>

          {detailLoading ? (
            <div className="py-8 text-center text-muted-foreground">
              Loading details...
            </div>
          ) : selectedSubmission ? (
            <div className="space-y-6">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-1">
                  <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                    Guest Name
                  </p>
                  <p
                    className="font-medium flex items-center gap-2"
                    data-testid="text-detail-guest-name"
                  >
                    <User className="w-4 h-4 text-muted-foreground" />
                    {selectedSubmission.guestName}
                  </p>
                </div>
                <div className="space-y-1">
                  <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                    Apartment
                  </p>
                  <p className="font-medium" data-testid="text-detail-apartment">
                    {getApartmentName(selectedSubmission.apartmentId)}
                  </p>
                </div>
                <div className="space-y-1">
                  <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                    Phone
                  </p>
                  <p
                    className="flex items-center gap-2"
                    data-testid="text-detail-phone"
                  >
                    <Phone className="w-4 h-4 text-muted-foreground" />
                    {selectedSubmission.guestPhone}
                  </p>
                </div>
                <div className="space-y-1">
                  <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                    Email
                  </p>
                  <p
                    className="flex items-center gap-2"
                    data-testid="text-detail-email"
                  >
                    <Mail className="w-4 h-4 text-muted-foreground" />
                    {selectedSubmission.guestEmail || "Not provided"}
                  </p>
                </div>
                <div className="space-y-1">
                  <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                    Date / Time
                  </p>
                  <p
                    className="flex items-center gap-2"
                    data-testid="text-detail-timestamp"
                  >
                    <Calendar className="w-4 h-4 text-muted-foreground" />
                    {formatDate(selectedSubmission.timestamp)}
                  </p>
                </div>
                <div className="space-y-1">
                  <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                    FOB Status
                  </p>
                  <Badge
                    variant={hasFob(selectedSubmission) ? "default" : "secondary"}
                    data-testid="badge-detail-fob-status"
                  >
                    <CreditCard className="w-3 h-3 mr-1" />
                    {hasFob(selectedSubmission) ? "FOB Submitted" : "No FOB"}
                  </Badge>
                </div>
              </div>

              {hasFob(selectedSubmission) && (
                <div className="space-y-3">
                  <p className="text-sm font-medium text-muted-foreground uppercase tracking-wide">
                    FOB Photos
                  </p>
                  <div className="grid grid-cols-2 gap-4">
                    {selectedSubmission.fobFrontUrl && (
                      <div className="space-y-1">
                        <p className="text-xs text-muted-foreground">Front</p>
                        <div
                          className="border-2 border-primary/50 rounded-md overflow-hidden cursor-pointer"
                          data-testid="img-fob-front"
                          onClick={() =>
                            setEnlargedImage(selectedSubmission.fobFrontUrl!)
                          }
                        >
                          <img
                            src={selectedSubmission.fobFrontUrl}
                            alt="FOB Front"
                            className="w-full h-40 object-cover"
                          />
                        </div>
                      </div>
                    )}
                    {selectedSubmission.fobBackUrl && (
                      <div className="space-y-1">
                        <p className="text-xs text-muted-foreground">Back</p>
                        <div
                          className="border-2 border-primary/50 rounded-md overflow-hidden cursor-pointer"
                          data-testid="img-fob-back"
                          onClick={() =>
                            setEnlargedImage(selectedSubmission.fobBackUrl!)
                          }
                        >
                          <img
                            src={selectedSubmission.fobBackUrl}
                            alt="FOB Back"
                            className="w-full h-40 object-cover"
                          />
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {!!selectedSubmission.checklistResults &&
                Array.isArray(selectedSubmission.checklistResults) &&
                (selectedSubmission.checklistResults as ChecklistResult[]).length > 0 && (
                  <div className="space-y-3">
                    <p className="text-sm font-medium text-muted-foreground uppercase tracking-wide">
                      Checklist Results
                    </p>
                    <div className="space-y-2">
                      {(selectedSubmission.checklistResults as ChecklistResult[]).map(
                        (item, idx) => (
                          <div
                            key={idx}
                            className="flex items-center gap-2 text-sm"
                            data-testid={`checklist-item-${idx}`}
                          >
                            {item.isChecked ? (
                              <CheckCircle2 className="w-4 h-4 text-green-500 flex-shrink-0" />
                            ) : (
                              <XCircle className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                            )}
                            <span>Item #{item.itemId}</span>
                            <Badge
                              variant={item.isChecked ? "default" : "secondary"}
                              className="text-xs ml-auto"
                            >
                              {item.isChecked ? "Checked" : "Unchecked"}
                            </Badge>
                          </div>
                        )
                      )}
                    </div>
                  </div>
                )}

              {!!selectedSubmission.photoResults &&
                Array.isArray(selectedSubmission.photoResults) &&
                (selectedSubmission.photoResults as PhotoResult[]).length > 0 && (
                  <div className="space-y-3">
                    <p className="text-sm font-medium text-muted-foreground uppercase tracking-wide">
                      Photo Results
                    </p>
                    {(selectedSubmission.photoResults as PhotoResult[]).map(
                      (photo, idx) => (
                        <div key={idx} className="space-y-2">
                          <p className="text-xs text-muted-foreground flex items-center gap-1">
                            <ImageIcon className="w-3 h-3" />
                            Requirement #{photo.requirementId}
                          </p>
                          <div className="grid grid-cols-3 gap-2">
                            {photo.urls.map((url, urlIdx) => (
                              <div
                                key={urlIdx}
                                className="rounded-md overflow-hidden cursor-pointer border"
                                data-testid={`img-photo-${idx}-${urlIdx}`}
                                onClick={() => setEnlargedImage(url)}
                              >
                                <img
                                  src={url}
                                  alt={`Photo ${urlIdx + 1}`}
                                  className="w-full h-24 object-cover"
                                />
                              </div>
                            ))}
                          </div>
                        </div>
                      )
                    )}
                  </div>
                )}
            </div>
          ) : null}
        </DialogContent>
      </Dialog>

      <Dialog
        open={!!enlargedImage}
        onOpenChange={() => setEnlargedImage(null)}
      >
        <DialogContent className="max-w-3xl p-2">
          {enlargedImage && (
            <img
              src={enlargedImage}
              alt="Enlarged view"
              className="w-full rounded-md"
              data-testid="img-enlarged"
            />
          )}
        </DialogContent>
      </Dialog>

      <DeleteConfirmDialog
        open={deleteConfirmSubmission !== null}
        onOpenChange={open => { if (!open) setDeleteConfirmSubmission(null); }}
        title="Delete this checkout submission?"
        description={deleteConfirmSubmission ? <>This will permanently delete the checkout submission for <strong>{deleteConfirmSubmission.guestName || "this guest"}</strong>{deleteConfirmSubmission.apartmentId ? <> at <strong>{getApartmentName(deleteConfirmSubmission.apartmentId)}</strong></> : null}. All guest photos and checklist responses will be lost. This cannot be undone.</> : null}
        confirmLabel="Delete Submission"
        pending={deleteSubmission.isPending}
        onConfirm={() => {
          if (!deleteConfirmSubmission) return;
          deleteSubmission.mutate(deleteConfirmSubmission.id, {
            onSuccess: () => toast({ title: "Submission deleted" }),
            onError: () => toast({ title: "Error", description: "Failed to delete submission", variant: "destructive" }),
          });
          setDeleteConfirmSubmission(null);
        }}
      />
    </AdminLayout>
  );
}
